import { useActiveModal, useModals } from '@frontend/modal';
import { MobileWizardStateData, MobileWizardStateRecord, clearData, resetWizard, updateCurrentScreen, updateData } from '@frontend/wizard/mobile';
import { ScreenTrigger, ScreenType, Step } from '@frontend/wizard/types';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../redux/store';

const TERMINAL_SCREENS = new Set<ScreenType>([ScreenType.HOME, ScreenType.PREVIOUS, ScreenType.START_SCREEN]);

export interface ScreenProps {
    navigate: NavigateFunction;
    dispatch: ThunkDispatch<any, any, Action>;
    wizardId: string | undefined;
    store: MobileWizardStateRecord | null;
    screen_type: ScreenType | undefined;
    wizardData: MobileWizardStateData;
    updateWizardData: (data: Partial<MobileWizardStateData>) => void;
    onSubmit: (nextStep?: Step) => void;
    onHome: () => void;
    onContinue: () => void;
    continueEnabled: boolean;
    onBack: () => void;
    backEnabled: boolean;
    finalStep: boolean;
    onCompleteWizard: () => Promise<void>;
}

export const useScreen = (wizardId: string): ScreenProps => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const wizardStore = useAppSelector((state) => state.modal_wizards);
    const store = wizardId ? wizardStore[wizardId] : null;
    const screen_type = store?.currentScreen;
    const { closeModal, closeTopModal } = useModals();
    const { modalId, registerCleanup } = useActiveModal();

    const updateWizardData = (data: Partial<MobileWizardStateData>) => {
        if (store && wizardId) {
            dispatch(
                updateData({
                    key: wizardId,
                    data: { ...data }
                })
            );
        }
    };

    const clearWizardCache = () => {
        if (wizardId) {
            dispatch(clearData(wizardId));
        }
    };

    const navigateToScreen = (screen: ScreenType) => {
        dispatch(updateCurrentScreen({ key: wizardId, screen }));
    };

    const goBackInHistory = () => {
        const history = store?.history ?? [];
        if (history.length === 0) {
            onHome();
            return;
        }

        navigateToScreen(history[history.length - 1] as ScreenType);
    };

    const onCompleteWizard = async () => {
        await registerCleanup(modalId, undefined);
        dispatch(resetWizard(modalId));
        closeModal(modalId);
    };

    const onHome = () => {
        closeTopModal();
    };

    const onSubmit = (nextStep?: Step) => {
        if (nextStep) {
            if (TERMINAL_SCREENS.has(nextStep.next_screen)) {
                void onCompleteWizard();
                return;
            }
            navigateToScreen(nextStep.next_screen);
            return;
        }

        const submitStep = store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT);
        if (submitStep) {
            if (TERMINAL_SCREENS.has(submitStep.next_screen)) {
                void onCompleteWizard();
                return;
            }
            navigateToScreen(submitStep.next_screen);
            return;
        }

        void onCompleteWizard();
    };

    const backStep = store?.steps.find((step) => step.screen === screen_type && [ScreenTrigger.BACK, ScreenTrigger.BACK_TO].includes(step.trigger));

    const onBack = () => {
        if (!backStep) {
            goBackInHistory();
            return;
        }

        if (backStep.trigger === ScreenTrigger.BACK) {
            if (backStep.screen === store?.wizard.start_screen) onHome();
            else goBackInHistory();
            return;
        }

        if (backStep.trigger === ScreenTrigger.BACK_TO) {
            if (backStep.next_screen === ScreenType.HOME) onHome();
            else if (backStep.next_screen === ScreenType.START_SCREEN && store?.wizard.start_screen) navigateToScreen(store.wizard.start_screen);
            else if (backStep.next_screen === ScreenType.PREVIOUS) {
                if (backStep.screen === store?.wizard.start_screen) onHome();
                else goBackInHistory();
                return;
            } else navigateToScreen(backStep.next_screen);
        }
    };

    const continueStep = store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.CONTINUE_TO);

    const onContinue = () => {
        if (!continueStep) return;

        clearWizardCache();

        if (continueStep.next_screen === ScreenType.HOME) onHome();
        else if (continueStep.next_screen === ScreenType.START_SCREEN && store?.wizard.start_screen) navigateToScreen(store.wizard.start_screen);
        else if (continueStep.next_screen === ScreenType.PREVIOUS) {
            if (continueStep.screen === store?.wizard.start_screen) onHome();
            else goBackInHistory();
        } else navigateToScreen(continueStep.next_screen);
    };

    const submitStep = store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT);

    return {
        navigate,
        dispatch,
        wizardId,
        store,
        screen_type,
        wizardData: store?.data ?? ({} as MobileWizardStateData),
        updateWizardData,
        onSubmit,
        onHome,
        onContinue,
        continueEnabled: !!continueStep,
        onBack,
        backEnabled: !!backStep || (store?.history?.length ?? 0) > 0,
        finalStep: !submitStep,
        onCompleteWizard
    };
};
